import React, { useContext, useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Box, Optional, Media, useMediaQueries } from '@honda-canada/design-system-react';
import { useLocation } from 'react-router-dom';

import DealerCard from '../DealerCard';
import DealerNoResultAlert from './DealerNoResultAlert';
import Disclaimer from '../ProductLineDisclaimer';
import themeStyles from './DealerCardsList.style';
import useSharedAppsUrlName from '../../hooks/useSharedAppsUrlName';
import { DealersListContext } from '../DealersListContext';
import { leadClientURLsMap } from '../../utils/constants';

const DealerCardsWrapper = themeStyles.apply(Box, 'DealerCardsWrapper');
const DealerListContainer = themeStyles.apply(Box, 'DealerListContainer');
const DealerCardsBox = themeStyles.apply(Box, 'DealerCardsBox');

const DealerCardsList = ({ setIsDealerCardScrolled, dealerCardsListRef, inputHeight }) => {
  const [updatedDealersList, setUpdatedDealersList] = useState([]);
  const [showSelectDealerCTA, setShowSelectDealerCTA] = useState(false);
  const {
    setIsOpen,
    dealersList,
    setDealerModalOpen,
    selectedDealer,
    hoveredDealer,
    selectedPlace,
    setHoveredDealer,
    setSelectedDealer,
    isDealerModalOpen,
    isLoading,
  } = useContext(DealersListContext);
  const { isDesktop } = useMediaQueries();

  const { pathname, search } = useLocation();
  const pathnameSegments = pathname.split('/');
  const currentURLPath = pathnameSegments.at(-1);
  const currentURLName = useSharedAppsUrlName(currentURLPath);
  const urlParams = {
    queryParams: search,
    currentURLName,
  };

  useEffect(() => {
    setShowSelectDealerCTA(!!currentURLPath && !!leadClientURLsMap[currentURLName]);
  }, []);

  useEffect(() => {
    if (selectedDealer) {
      if (dealerCardsListRef?.current) {
        dealerCardsListRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }

      // TODO: The sorting logic for dealer list to be updated
      const sortedDealerList = [...dealersList].sort((a, b) => {
        if (a.id === selectedDealer.id) {
          return -1;
        }
        if (b.id === selectedDealer.id) {
          return 1;
        }
        return 0;
      });
      return setUpdatedDealersList(sortedDealerList);
    }

    return setUpdatedDealersList(dealersList);
  }, [selectedDealer, dealersList, dealerCardsListRef]);

  const handleOnScroll = () => {
    if (dealerCardsListRef?.current) {
      const currentScrollPosition = dealerCardsListRef.current?.scrollTop;
      // only when clientHeight is smaller than scrollHeight, list is scrollable
      const isScrollable = dealerCardsListRef.current?.clientHeight < dealerCardsListRef.current?.scrollHeight;
      if (currentScrollPosition === 0 && isScrollable) {
        setIsDealerCardScrolled(false);
        if (isDesktop) {
          setIsOpen(false);
        }
      } else if (currentScrollPosition > 0) {
        setIsDealerCardScrolled(true);
        setIsOpen(false);
      }
    }
  };

  return (
    <DealerCardsWrapper
      isHidden={isDealerModalOpen}
      ref={dealerCardsListRef}
      onScroll={handleOnScroll}
      data-testid="cy-dealer-list"
      inputHeight={inputHeight}
    >
      <DealerCardsBox>
        <Media greaterThanOrEqual="desktop">
          <Optional when={selectedPlace && !isLoading}>
            <Disclaimer />
          </Optional>
          <Optional when={!dealersList?.length && selectedPlace && !isLoading}>
            <DealerNoResultAlert />
          </Optional>
        </Media>
        <Optional when={updatedDealersList?.length}>
          <DealerListContainer>
            {updatedDealersList?.map(dealer => (
              <Fragment key={dealer?.id}>
                <DealerCard
                  setDealerModalOpen={setDealerModalOpen}
                  setSelectedDealer={setSelectedDealer}
                  setHoveredDealer={setHoveredDealer}
                  key={dealer?.id}
                  dealer={dealer}
                  distance={dealer?.drivingDistance}
                  isCardSelected={dealer?.id === selectedDealer?.id}
                  isCardHovered={dealer?.id === hoveredDealer?.id}
                  showSelectDealerCTA={showSelectDealerCTA}
                  urlParams={urlParams}
                />
              </Fragment>
            ))}
          </DealerListContainer>
        </Optional>
      </DealerCardsBox>
    </DealerCardsWrapper>
  );
};

DealerCardsList.defaultProps = {
  setIsDealerCardScrolled: () => {},
};

DealerCardsList.propTypes = {
  setIsDealerCardScrolled: PropTypes.func,
  dealerCardsListRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })])
    .isRequired,
  inputHeight: PropTypes.number,
};

export default DealerCardsList;
